/**
 * Bottom nav — shown only while inside the body-recomp app (AppShell gates it):
 *   [Log · Progress]
 * Score and Rewards live in the OS HUD; Home is the HUD's top-left logo. So the
 * bottom bar is purely the app's own pages.
 *
 * Active item = blue text + a 3px blue indicator bar at the top edge. Flat
 * lucide line icons, no shadow. Fixed at the bottom with safe-area padding;
 * the bar itself is centered to the same max-width as the content for desktop.
 */
import { NavLink } from "react-router-dom";
import { Plus, TrendingUp, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const items: NavItem[] = [
  { to: "/log", label: "Log", icon: Plus },
  { to: "/progress", label: "Progress", icon: TrendingUp },
];

export function BottomNav() {
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <ul className="mx-auto flex max-w-2xl items-stretch justify-between">
        {items.map(({ to, label, icon: Icon }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                cn(
                  "relative flex min-h-16 flex-col items-center justify-center gap-1 px-1 py-2 text-xs font-medium transition-colors",
                  isActive ? "font-semibold text-primary" : "text-muted-foreground hover:text-foreground",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute top-0 left-1/2 h-[3px] w-5 -translate-x-1/2 rounded-b-[3px] bg-primary"
                    />
                  )}
                  <Icon className="h-6 w-6" strokeWidth={1.8} />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
